import { gql } from '@apollo/client';

/**
 * Announcement banners (Hasura table ri_apps_prd.announcements).
 *
 * Two audiences, two permission shapes: every signed-in role reads ACTIVE rows only (the select
 * permission's row filter enforces `is_active`; the banner query's `where` mirrors it so it
 * documents its own contract), while superadmins additionally hold an UNFILTERED select plus
 * insert/update/delete for the manager screen (sections/superadmin/announcements.js). The
 * schedule window, role and state targeting and per-browser dismissal are deliberately evaluated
 * CLIENT-side (see components/announcement-banner) — the table holds a handful of rows, and
 * keeping the query static avoids expressing now()/array-membership in Hasura where-clauses.
 */

const ANNOUNCEMENT_FIELDS = gql`
  fragment AnnouncementFields on ri_apps_prd_announcements {
    announcement_id
    title
    message
    cta_label
    cta_path
    variant
    roles
    states
    starts_at
    ends_at
    dismissible
    is_active
    created_at
    updated_at
  }
`;

export const GET_ANNOUNCEMENTS = gql`
  query GetAnnouncements {
    rows: ri_apps_prd_announcements(
      where: { is_active: { _eq: true } }
      order_by: { announcement_id: desc }
    ) {
      announcement_id
      title
      message
      cta_label
      cta_path
      variant
      roles
      states
      starts_at
      ends_at
      dismissible
    }
  }
`;

/** Manager list — every row, newest first. Needs the superadmin's unfiltered select permission. */
export const GET_ALL_ANNOUNCEMENTS = gql`
  query GetAllAnnouncements {
    rows: ri_apps_prd_announcements(order_by: { announcement_id: desc }) {
      ...AnnouncementFields
    }
  }
  ${ANNOUNCEMENT_FIELDS}
`;

export const INSERT_ANNOUNCEMENT = gql`
  mutation InsertAnnouncement($object: ri_apps_prd_announcements_insert_input!) {
    row: insert_ri_apps_prd_announcements_one(object: $object) {
      ...AnnouncementFields
    }
  }
  ${ANNOUNCEMENT_FIELDS}
`;

export const UPDATE_ANNOUNCEMENT = gql`
  mutation UpdateAnnouncement($id: Int!, $changes: ri_apps_prd_announcements_set_input!) {
    row: update_ri_apps_prd_announcements_by_pk(
      pk_columns: { announcement_id: $id }
      _set: $changes
    ) {
      ...AnnouncementFields
    }
  }
  ${ANNOUNCEMENT_FIELDS}
`;

export const DELETE_ANNOUNCEMENT = gql`
  mutation DeleteAnnouncement($id: Int!) {
    row: delete_ri_apps_prd_announcements_by_pk(announcement_id: $id) {
      announcement_id
    }
  }
`;
